import { createElement, useRef, useState } from 'react'
import Immutable from 'immutable'
import cc from 'classcat'
import loadable from '@loadable/component'

import { createEmptyImage } from '../image/ImagePlugin'
import { renderImage as sharedRenderImage } from '../image/imageRendering'
import { useEditorSpotlight } from '../../../../EditorSpotlight'
import { useTranslate } from '../../../../../utils/translate'
import HeroImageOverlay from './HeroImageOverlay'
import useHelpCenterLink from '../../../../../utils/hooks/useHelpCenterLink'

const SettingsLayer = loadable(() => import(/* webpackChunkName: "editor" */ '../../SettingsLayer'))
const Settings = loadable(() => import(/* webpackChunkName: "editor" */ './HeroImageSettings'))

const defaultData = Immutable.fromJS({
  src: '',
  width: null,
  height: null,
  alt: '',
  link: '',
  opentab: false,
  text: '',
  buttontext: '',
  headline: '',
  buttonenabled: false,

  // Reference to the original image (image before any modifications).
  // Initially, after image upload, this is always `undefined`. The property is
  // first set on first image editor "init" event. Before that, the "src"
  // property has the reference to the original image.
  // See `ImageEditor` use below.
  refSrc: '',
  layout: 'overlay',
  layoutSide: 'left',
  overlayWidth: 50,
  overlayBlur: false,

  // Image editing settings (used with the image editor).
  edit: {
    aspectRatio: { label: '16:9', value: 16 / 9 },
    offset: [0, 0], // [x, y]
    zoom: 1,
  },
})

export default function HeroImagePlugin({
  config,
  editorView,
  editorMode,
  editAction,
  data = defaultData,
  onDataChange,
  onSave,
  onCancel,
  onEdit,
}: Readonly<WorkspacePluginProps>) {
  const [error, setError] = useState<Error | null>(null)
  const [imageEditPreviewSrc, setImageEditPreviewSrc] = useState<string>()
  const ref = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const helpCenterUrl = useHelpCenterLink('IMAGE_FORMATS')
  const [isActive, setIsActive] = useState(false)
  // Add a new ref for tracking the image height
  const [imageHeight, setImageHeight] = useState<number | null>(null)

  const t = useTranslate('interface')

  const isUnsupportedMediaType = error?.code === 415
  const isEmpty = !data.get('src')
  const isLinked = Boolean(data.get('link'))
  const pluginActiveClasses = cc([
    'dali-plugin-heroimage',
    {
      'dali-grid-element-highlighted': isActive,
    },
  ])

  // Optimistically put the image placeholder element into spotlight while the
  // initial image is uploading and before the image editor is rendering at the
  // same position in spotlight. Depending on the internet connection, the
  // initial image upload can take a while and we don’t want the user to be
  // able to interact with other elements in the meantime.
  const imagePlaceholderElementRef = useRef<HTMLDivElement>(null)
  const [isInitialImageUploading, setIsInitialImageUploading] = useState(false)
  useEditorSpotlight(isInitialImageUploading ? imagePlaceholderElementRef.current : null)

  const emptyImage = createEmptyImage({
    error,
    helpCenterUrl,
    t,
    data,
    onSave,
    onEdit,
    config,
    setError,
    setImageEditPreviewSrc,
    setIsInitialImageUploading,
    imagePlaceholderElementRef,
    isUnsupportedMediaType,
    isHeroImage: true,
  })

  /**
   * Renders the image in the storefront or in the editor. Renders in image
   * editor mode when the edit action is "editImage".
   *
   * The image editor uses the original image as the source for editing:
   *
   * - Initially, the "src" property is always set to the original image.
   * - After the first image editor "init" event, the "refSrc" property is set
   *   to the original image and the "src" property is set to the edited image.
   */
  function renderImage() {
    const aspectRatioMap = {
      '3:1': 3 / 1,
      '3:2': 3 / 2,
      '4:3': 4 / 3,
      '16:9': 16 / 9,
    }

    return sharedRenderImage({
      data,
      config,
      editorView,
      editAction,
      imageEditPreviewSrc,
      imageRef,
      aspectRatioMap,
      defaultData,
      onSave,
      onCancel,
      onDataChange,
      setImageEditPreviewSrc,
      OverlayComponent: HeroImageOverlay,
      overlayProps: {
        overlaySettings: { overlayWidth: data.get('overlayWidth'), overlayBlur: data.get('overlayBlur') },
        layoutSettings: { layout: data.get('layout'), layoutSide: data.get('layoutSide') },
      },
      wrapperClassName: `dali-image-wrapper ${data.get('layoutSide')}`,
      wrapperStyle: {
        '--image-height': imageHeight ? `${imageHeight}px` : 'auto',
      } as React.CSSProperties,
      imageClassName: 'dali-image',
      imageStyle: { display: 'flex' },
      isLinked,
      setImageHeight,
    })
  }

  if (editorView) {
    return (
      <div className={pluginActiveClasses} ref={ref}>
        {isEmpty ? emptyImage : renderImage()}
        <SettingsLayer
          className="dali-settingslayer-heroimage"
          onActiveStateChange={setIsActive}
          open={editorMode === 'edit' && editAction !== 'editImage'}
          virtualRef={ref}
          side="right"
          onEscapeKeyDown={onCancel}
        >
          {({ renderLayout }) => {
            return createElement(Settings, {
              data,
              onDataChange,
              config,
              onCancel,
              renderLayout,
              onSave: (data) => {
                setError(null)
                onSave(data)
              },
            })
          }}
        </SettingsLayer>
      </div>
    )
  }

  return !isEmpty ? <div className={pluginActiveClasses}>{renderImage()}</div> : null
}
